import { type ReactNode, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ENGLISH_LANGUAGE_CODE } from '@duke-hq/shared-constants/courseTranslationLanguages';

export type PreviewVersion = 'published' | 'draft';

// Undefined when the picker shouldn't show (draft mode).
export type PreviewLanguageControls = {
  value: string;
  onChange: (language: string) => void;
};

type PreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  hasPublishedVersion: boolean;
  children: (args: {
    version: PreviewVersion;
    language: PreviewLanguageControls | undefined;
    versionToggle: ReactNode;
  }) => ReactNode;
};

export function PreviewModal({
  isOpen,
  onClose,
  title,
  hasPublishedVersion,
  children,
}: PreviewModalProps) {
  const [version, setVersion] = useState<PreviewVersion>(
    hasPublishedVersion ? 'published' : 'draft',
  );
  const [language, setLanguage] = useState<string>(ENGLISH_LANGUAGE_CODE);

  // The modal stays mounted across opens, so reset per open: default to published,
  // and clear the language (one carried from another resource may not exist here,
  // which would silently render English).
  useEffect(() => {
    if (isOpen) {
      setVersion(hasPublishedVersion ? 'published' : 'draft');
      setLanguage(ENGLISH_LANGUAGE_CODE);
    }
  }, [isOpen, hasPublishedVersion]);

  // Translations are produced per published version, so the picker only applies
  // in published mode; draft always previews the source English.
  const showLanguagePicker = version === 'published';
  const languageControls = showLanguagePicker
    ? { value: language, onChange: setLanguage }
    : undefined;
  const activeLanguage = showLanguagePicker ? language : ENGLISH_LANGUAGE_CODE;

  const versionToggle = (
    <ButtonGroup size="sm" isAttached variant="outline" flexShrink={0}>
      <Button
        onClick={() => setVersion('published')}
        isDisabled={!hasPublishedVersion}
        colorScheme="gray"
        variant={version === 'published' ? 'solid' : 'outline'}
      >
        <Trans>Published</Trans>
      </Button>
      <Button
        onClick={() => setVersion('draft')}
        colorScheme="gray"
        variant={version === 'draft' ? 'solid' : 'outline'}
      >
        <Trans>Draft</Trans>
      </Button>
    </ButtonGroup>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', lg: '3xl' }}>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <Text fontWeight="semibold" fontSize="lg" noOfLines={1} pr="8">
            {t`Preview: ${title}`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6" overflowY="auto">
          <Alert status="info" mb="4" borderRadius="md">
            <AlertIcon />
            <Trans>Preview mode — no progress or answers are saved</Trans>
          </Alert>
          <Box key={`${version}:${activeLanguage}`}>
            {children({ version, language: languageControls, versionToggle })}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
